import React, { useMemo, useState } from "react";
import MarketplaceCoinIcon from "./MarketplaceCoinIcon";
import { grantAdminMarketplaceAward } from "./marketplaceApi";
import { loadUserMatrixSettings } from "./userMatrixStorage";

const MAX_NOTE_LENGTH = 280;

/**
 * Admin form to assign V-coins to a user with a short message.
 * @param {{ onGranted?: (result: object) => void }} props
 */
export default function MarketplaceManualGrantForm({ onGranted }) {
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("10");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const emailSuggestions = useMemo(() => {
    const matrix = loadUserMatrixSettings();
    const users = Array.isArray(matrix?.users) ? matrix.users : [];
    return [
      ...new Set(
        users
          .map((u) => String(u?.email || "").trim().toLowerCase())
          .filter(Boolean)
      ),
    ].sort();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    const userEmail = String(email || "").trim().toLowerCase();
    const coins = Math.trunc(Number(amount) || 0);
    const message = String(note || "").trim();
    if (!userEmail) {
      setError("Enter the recipient’s email.");
      return;
    }
    if (coins <= 0) {
      setError("Amount must be a positive whole number.");
      return;
    }
    if (!message) {
      setError("Add a short message for the award.");
      return;
    }
    setBusy(true);
    try {
      const result = await grantAdminMarketplaceAward({
        userEmail,
        amount: coins,
        note: message,
      });
      setSuccess(
        `Awarded ${coins.toLocaleString()} V-coin${coins === 1 ? "" : "s"} to ${result.userEmail}.`
      );
      setNote("");
      setAmount("10");
      if (typeof onGranted === "function") onGranted(result);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not assign award.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="marketplace-manual-grant" aria-labelledby="marketplace-manual-grant-title">
      <div className="marketplace-manual-grant-head">
        <h2 id="marketplace-manual-grant-title" className="marketplace-manual-grant-title">
          Assign award
        </h2>
        <p className="marketplace-manual-grant-lead">
          Credit any user with V-coins and a short message. The grant appears in their transaction history.
        </p>
      </div>

      <form className="marketplace-manual-grant-form" onSubmit={onSubmit}>
        <label className="marketplace-manual-grant-field">
          <span className="marketplace-manual-grant-label">Recipient email</span>
          <input
            type="email"
            list="marketplace-manual-grant-emails"
            className="marketplace-manual-grant-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            autoComplete="off"
            required
            disabled={busy}
          />
          <datalist id="marketplace-manual-grant-emails">
            {emailSuggestions.map((em) => (
              <option key={em} value={em} />
            ))}
          </datalist>
        </label>

        <label className="marketplace-manual-grant-field marketplace-manual-grant-field--amount">
          <span className="marketplace-manual-grant-label">Amount</span>
          <span className="marketplace-manual-grant-amount-wrap">
            <MarketplaceCoinIcon size={16} />
            <input
              type="number"
              className="marketplace-manual-grant-input marketplace-manual-grant-input--amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={1}
              max={100000}
              step={1}
              required
              disabled={busy}
            />
          </span>
        </label>

        <label className="marketplace-manual-grant-field marketplace-manual-grant-field--note">
          <span className="marketplace-manual-grant-label">
            Message{" "}
            <span className="marketplace-manual-grant-count">
              {note.length}/{MAX_NOTE_LENGTH}
            </span>
          </span>
          <input
            type="text"
            className="marketplace-manual-grant-input"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE_LENGTH))}
            placeholder="e.g. Great work on the Q2 push"
            maxLength={MAX_NOTE_LENGTH}
            required
            disabled={busy}
          />
        </label>

        <div className="marketplace-manual-grant-actions">
          <button type="submit" className="marketplace-manual-grant-submit" disabled={busy}>
            {busy ? "Assigning…" : "Assign coins"}
          </button>
        </div>
      </form>

      {error ? (
        <p className="user-profile-settings-error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="user-profile-settings-success" role="status">
          {success}
        </p>
      ) : null}
    </section>
  );
}
